import { subDays } from "date-fns";

import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { typedQueryClient, useTypedQuery } from "hooks";
import { QUESTION_RESULT_STATUSES } from "models";
import { LeadsQueryKeys } from "modules/LeadsDashboard/queryKeys";
import { LEADS_SNOOZED_STORAGE_KEYS } from "modules/LeadsDashboard/storageKeys";
import { PAGE_SIZE, getPreference } from "utils/common";
import { toDate } from "utils/dateHelperFunctions";

import type { DealerLocation } from "models";
import type { PossibleQuestionStatusOptions } from "modules/LeadsDashboard/hooks";
import type { LeadsSnoozedFilters } from "modules/LeadsDashboard/queryKeys";

export const QUESTION_STATUSES_DEFAULT_STATUSES: PossibleQuestionStatusOptions[] = [
  QUESTION_RESULT_STATUSES.OKWithRemarks,
  QUESTION_RESULT_STATUSES.Advice,
  QUESTION_RESULT_STATUSES.Necessary
];

const getDateRangePreference = (key: LEADS_SNOOZED_STORAGE_KEYS, defaultStart: Date | null, defaultEnd: Date | null) => {
  const { start, end } = getPreference(key, { start: defaultStart, end: defaultEnd });
  return { start: start ? toDate(start) : null, end: end ? toDate(end) : null };
};

export const getDefaultSnoozedFilters = (selectedLocation?: DealerLocation | null): LeadsSnoozedFilters => {
  const appointmentDate = getDateRangePreference(LEADS_SNOOZED_STORAGE_KEYS.AppointmentDatePreference, subDays(new Date(), 30), new Date());
  const snoozeDate = getDateRangePreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeDatePreference, null, null);
  const updatedOnDate = getDateRangePreference(LEADS_SNOOZED_STORAGE_KEYS.UpdatedOnDatePreference, null, null);

  return {
    page: 1,
    dealer_ids: getPreference(LEADS_SNOOZED_STORAGE_KEYS.DealerPreference, []),
    appointment_date_from: appointmentDate.start,
    appointment_date_to: appointmentDate.end,
    search_term: "",
    is_lease_company: getPreference(LEADS_SNOOZED_STORAGE_KEYS.RegVinPreference, null),
    location_ids: getPreference(LEADS_SNOOZED_STORAGE_KEYS.LocationPreference, selectedLocation?.id ? [selectedLocation.id] : []),
    customer_approved: getPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerApprovedPreference, false),
    interventions: getPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionsPreference, false),
    statuses: getPreference(LEADS_SNOOZED_STORAGE_KEYS.QuestionStatusPreference, null),
    questions: getPreference(LEADS_SNOOZED_STORAGE_KEYS.InterventionQuestionResultPreference, null),
    tags: getPreference(LEADS_SNOOZED_STORAGE_KEYS.TagsPreference, null),
    snooze_date_from: snoozeDate.start,
    snooze_date_to: snoozeDate.end,
    snooze_department_id: getPreference(LEADS_SNOOZED_STORAGE_KEYS.DepartmentPreference, null),
    snooze_status_id: getPreference(LEADS_SNOOZED_STORAGE_KEYS.SnoozeStatusPreference, null),
    snooze_type_id: getPreference(LEADS_SNOOZED_STORAGE_KEYS.TaskPreference, null),
    last_customer_ok_is_me: getPreference(LEADS_SNOOZED_STORAGE_KEYS.CustomerOkUserPreference, false),
    updated_on_from: updatedOnDate.start,
    updated_on_to: updatedOnDate.end,
    page_size: getPreference(LEADS_SNOOZED_STORAGE_KEYS.PageSizePreference, PAGE_SIZE.PageSize25)
  };
};

export const useLeadsSnoozedFilters = () => {
  const { selectedLocation } = useSelectedLocation();

  return useTypedQuery({ queryKey: LeadsQueryKeys.snoozedFilters, initialData: getDefaultSnoozedFilters(selectedLocation) }).data;
};

export const setLeadsSnoozedFilters = (newFilters: LeadsSnoozedFilters) => {
  const filters = typedQueryClient.getQueryData(LeadsQueryKeys.snoozedFilters);
  if (!filters) return;
  typedQueryClient.setQueryData(LeadsQueryKeys.snoozedFilters, { ...filters, ...newFilters });
};
